import { get } from 'svelte/store'
import { t } from '$i18n/i18n'
import { fetchUtils } from '$utils/fetchUtils'
import { addNotification } from '../stores'
import { NotificationType } from '$types/notification'
import type { AnalyticalEvaluation } from './types/analyticalEvaluation'
import type {
	DeclarationParameterEntry,
	DeclarationParameterPreview
} from './types/declarationParameter'

type FormattedParameter = { name: string; value: string }

type MaterialPropertyNode = {
	type?: string
	publicId?: string
	value?: unknown
	propertyListElementDTOs?: { publicId?: string; propertyChildren?: MaterialPropertyNode[] }[]
}

export function evaluateAnalysis(
	analyticTechnicalName: string,
	parameters: FormattedParameter[],
	values: unknown
): Promise<AnalyticalEvaluation> {
	return fetchUtils.post(`/api/analytics/evaluateAnalysis`, {
		analyticTechnicalName,
		parameters,
		values
	})
}

export function fetchDeclarationParameterPreview(
	analyticalId: string,
	payload: Record<string, unknown>
): Promise<DeclarationParameterPreview> {
	return fetchUtils.post(`/api/analysis/${analyticalId}/declarationParameterPreview`, payload)
}

const showSaveSuccessNotification = () => {
	addNotification({
		message: get(t)('Analytics.tool.saved.success'),
		type: NotificationType.SUCCESS
	})
}

/**
 * Saves the analysis, then — if entries were confirmed — mirrors the Materialeigenschaften modal save path:
 * fetches the parent lot, applies each entry (switches the classification select to the matched option and
 * writes the text into its field, addressed by path), refreshes the two comment fields, and POSTs the whole
 * lot back to {@code /api/lot/update/:1}. One success notification at the end.
 */
export const persistAnalysis = async (
	url: string,
	payload: Record<string, unknown>,
	entriesToApply: DeclarationParameterEntry[] | null,
	lotId: string | null
) => {
	await fetchUtils.post(url, payload)

	if (entriesToApply && entriesToApply.length > 0 && lotId) {
		try {
			await applyEntriesToLot(lotId, entriesToApply)
		} catch (error) {
			fetchUtils.catchErrorAndShowNotification()(error)

			return
		}
	}

	showSaveSuccessNotification()
}

async function applyEntriesToLot(lotId: string, entries: DeclarationParameterEntry[]) {
	const lot = await fetchUtils.get(`/api/lot/${lotId}`)

	if (!lot?.materialProperties) {
		return
	}

	const parsed = JSON.parse(lot.materialProperties)
	let mutated = false

	for (const entry of entries) {
		if (applyEntryByPath(parsed.data, entry.path, entry.valueToAppend)) {
			mutated = true
		}
	}

	if (!mutated) {
		return
	}

	lot.materialProperties = JSON.stringify(parsed)
	const [comment, commentReq] = await Promise.all([
		fetchUtils.post('/api/materialProperties/jsonToCommentString', parsed, false).catch(() => ''),
		fetchUtils
			.post('/api/materialProperties/jsonToCommentString/requiredOnly', parsed, false)
			.catch(() => '')
	])
	lot.materialPropertiesComment = comment ?? ''
	lot.materialPropertiesCommentWithoutOptional = commentReq ?? ''
	await fetchUtils.post(`/api/lot/update/${lotId}`, lot)
}

/**
 * Applies one preview entry: writes {@code value} into the STRING node addressed by {@code path} —
 * alternating select publicId / list-element publicId pairs, terminated by the STRING node's publicId
 * (built by the backend preview from the selected branches). A plain publicId lookup is NOT sufficient:
 * the same property definition (same publicId) is reused under several cascades, and values written outside
 * the selected branch are silently discarded by the backend on save. The final select's value is set to the
 * path's option defensively (idempotent — the backend only offers fields under selected options).
 */
function applyEntryByPath(
	nodes: MaterialPropertyNode[] | undefined,
	path: string[],
	value: string
): boolean {
	if (!Array.isArray(nodes) || path.length < 3 || path.length % 2 === 0) {
		return false
	}

	const [selectId, listElementId, ...rest] = path

	for (const node of nodes) {
		if (node?.publicId !== selectId) {
			continue
		}

		const listElement = (node.propertyListElementDTOs ?? []).find(
			(candidate) => candidate?.publicId === listElementId
		)

		if (!listElement) {
			continue
		}

		if (rest.length === 1) {
			const target = (listElement.propertyChildren ?? []).find(
				(child) => child?.type === 'STRING' && child?.publicId === rest[0]
			)

			if (!target) {
				return false
			}

			// switch the classification selection to the matched option
			if (node.type === 'MULTISELECT') {
				const current = Array.isArray(node.value) ? node.value : []

				if (!current.includes(listElementId)) {
					node.value = [...current, listElementId]
				}
			} else {
				node.value = [listElementId]
			}

			target.value = value

			return true
		}

		if (applyEntryByPath(listElement.propertyChildren, rest, value)) {
			return true
		}
	}

	return false
}
